import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { CompanyBaseInfo } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import type { SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import { isAppsSdksSignalEvent } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import { assertNever } from "app/pages/sales-intelligence/utils/errors/assertions";

import AdNetworkSignalEventInfoBox from "./AdNetworkSignalEventInfoBox";
import AppsSignalEventInfoBox from "./AppsSignalEventInfoBox";
import EcommerceSignalEventInfoBox from "./EcommerceSignalEventInfoBox";
import IntentSignalEventInfoBox from "./IntentSignalEventInfoBox/IntentSignalEventInfoBox";
import NewsSignalEventInfoBox from "./NewsSignalEventInfoBox";
import TechnologySignalEventInfoBox from "./TechnologySignalEventInfoBox";
import TrafficSignalEventInfoBox from "./TrafficSignalEventInfoBox";

type SignalEventInfoProps = {
    signal: SignalEventDto;
    companyInfo?: CompanyBaseInfo;
    signalsDescription: Readonly<SignalsDescription> | null;
    onCompanyLinkClick: (signalEvent: SignalEventDto) => void;
};

const SignalEventInfoBox = ({ signal, companyInfo, signalsDescription, onCompanyLinkClick }: SignalEventInfoProps) => {
    const companyName = companyInfo?.name || "";

    switch (signal.type) {
        case signalTypeMap.intent:
            return <IntentSignalEventInfoBox signal={signal} />;
        case signalTypeMap.news:
            return (
                <NewsSignalEventInfoBox
                    signal={signal}
                    onCompanyLinkClick={onCompanyLinkClick}
                    data-automation="signal-event-info-news"
                />
            );
        case signalTypeMap.technology:
            return (
                <TechnologySignalEventInfoBox
                    signal={signal}
                    companyName={companyName}
                    data-automation="signal-event-info-technology"
                />
            );
        case signalTypeMap.adNetwork:
            return (
                <AdNetworkSignalEventInfoBox
                    signal={signal}
                    companyName={companyName}
                    signalsDescription={signalsDescription}
                    data-automation="signal-event-info-ad-network"
                />
            );
        case signalTypeMap.traffic:
            return <TrafficSignalEventInfoBox signal={signal} />;
        case signalTypeMap.ecommerce:
            return (
                <EcommerceSignalEventInfoBox
                    signal={signal}
                    data-automation="signal-event-info-ecommerce"
                />
            );
        case signalTypeMap.apps:
            // A second Apps subtype would not be an AppsAppsSdks event; render nothing rather than mistype it.
            return isAppsSdksSignalEvent(signal) ? (
                <AppsSignalEventInfoBox
                    signal={signal}
                    companyName={companyName}
                    data-automation="signal-event-info-apps"
                />
            ) : null;
        case signalTypeMap.roleChanges:
            // The role change is fully described by the event title and its change-type tag.
            return null;
        default:
            return assertNever(signal, "renderSignalEventBody", "SignalEventInfoBox");
    }
};

export default SignalEventInfoBox;
